import React, { useEffect, useState } from 'react';
import { authFetch } from '../auth';

export default function AdminCabinetsView({ API_URL }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState(null); // id
  const [form, setForm] = useState({ name: '', filespath: '', fields: '' });
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', filespath: '', fields: '' });

  const load = async () => {
    setLoading(true); setErr('');
    try {
      const res = await authFetch(`${API_URL}/admin/cabinets`);
      if (!res.ok) throw new Error((await res.json().catch(()=>({}))).message || 'Failed to load cabinets');
      const j = await res.json();
      setRows(Array.isArray(j) ? j : []);
    } catch (e) { setErr(e?.message || 'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onEdit = (row) => {
    setEditing(row.id);
    setForm({ name: row.name || '', filespath: row.filespath || '', fields: row.fields || '' });
  };
  const onCancel = () => { setEditing(null); setForm({ name: '', filespath: '', fields: '' }); };
  const onCreate = async (e) => {
    e.preventDefault();
    setLoading(true); setErr('');
    try {
      const res = await authFetch(`${API_URL}/admin/cabinets`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(createForm)
      });
      if (!res.ok) throw new Error((await res.json().catch(()=>({}))).message || 'Failed to create cabinet');
      const body = await res.json().catch(()=>({}));
      await load();
      setCreateForm({ name: '', filespath: '', fields: '' });
      setCreating(false);
      try {
        window.spartaToast && window.spartaToast(body.storageWarning || 'Cabinet added', body.storageWarning ? 'warning' : 'success', 3000);
      } catch (_) {}
    } catch (e) { setErr(e?.message || 'Failed to create cabinet'); }
    finally { setLoading(false); }
  };
  const onSave = async (id) => {
    setLoading(true); setErr('');
    try {
      const res = await authFetch(`${API_URL}/admin/cabinets/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form)
      });
      if (!res.ok) throw new Error((await res.json().catch(()=>({}))).message || 'Failed to save');
      await load();
      // If active cabinet changed its name, update the header immediately
      try {
        const ares = await authFetch(`${API_URL}/cabinets/active`);
        if (ares.ok) {
          const active = await ares.json();
          if (active && active.name && typeof window !== 'undefined' && window.spartaSetCustomerName) {
            window.spartaSetCustomerName(active.name);
          }
        }
      } catch (_) {}
      setEditing(null);
      try { window.spartaToast && window.spartaToast('Cabinet updated', 'success', 2500); } catch (_) {}
    } catch (e) { setErr(e?.message || 'Failed to save'); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>Admin · Cabinets</h2>
      {err && <div style={errorBox}>{err}</div>}
      <div style={toolbar}>
        <button className="btn btn-primary" disabled={loading || creating} onClick={() => { setCreating(true); setEditing(null); }}>Add Cabinet</button>
      </div>
      {creating && (
        <form onSubmit={onCreate} style={createBox}>
          <div style={formGrid}>
            <label style={fieldLabel}>
              <span>Name</span>
              <input style={input} value={createForm.name} onChange={e=>setCreateForm(f=>({...f,name:e.target.value}))} autoFocus />
            </label>
            <label style={fieldLabel}>
              <span>Filespath</span>
              <input style={input} value={createForm.filespath} onChange={e=>setCreateForm(f=>({...f,filespath:e.target.value}))} />
            </label>
            <label style={fieldLabel}>
              <span>Fields</span>
              <input style={input} value={createForm.fields} onChange={e=>setCreateForm(f=>({...f,fields:e.target.value}))} />
            </label>
          </div>
          <div style={{ marginTop: 10 }}>
            <button type="submit" className="btn btn-primary" disabled={loading}>Save Cabinet</button>
            <button type="button" className="btn btn-clear" disabled={loading} style={{ marginLeft: 8 }} onClick={() => { setCreating(false); setCreateForm({ name: '', filespath: '', fields: '' }); }}>Cancel</button>
          </div>
        </form>
      )}
      <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Name</th>
              <th style={th}>Filespath</th>
              <th style={th}>Fields</th>
              <th style={th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} style={{ background: 'var(--card-bg)' }}>
                <td style={td}>
                  {editing === r.id
                    ? <input style={input} value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} />
                    : r.name}
                </td>
                <td style={{ ...td, fontFamily: 'monospace' }}>
                  {editing === r.id
                    ? <input style={{ ...input, width: '100%' }} value={form.filespath} onChange={e=>setForm(f=>({...f,filespath:e.target.value}))} />
                    : r.filespath}
                </td>
                <td style={td}>
                  {editing === r.id
                    ? <input style={{ ...input, width: '100%' }} value={form.fields} onChange={e=>setForm(f=>({...f,fields:e.target.value}))} />
                    : (r.fields || '')}
                </td>
                <td style={td}>
                  {editing === r.id ? (
                    <>
                      <button className="btn btn-primary" disabled={loading} onClick={()=>onSave(r.id)}>Save</button>
                      <button className="btn btn-clear" disabled={loading} style={{ marginLeft: 8 }} onClick={onCancel}>Cancel</button>
                    </>
                  ) : (
                    <button className="btn" disabled={loading} onClick={()=>onEdit(r)}>Edit</button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td style={{ ...td, textAlign: 'center' }} colSpan={4}>No cabinets</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const th = { textAlign: 'left', padding: '8px', background: 'var(--table-head-bg)', borderBottom: '1px solid var(--border)' };
const td = { padding: '8px', borderBottom: '1px solid var(--border)' };
const input = { padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 6 };
const errorBox = { padding: 10, border: '1px solid #e57373', background: '#ffebee', color: '#b71c1c', borderRadius: 6, marginBottom: 10 };
const toolbar = { display: 'flex', justifyContent: 'flex-end', marginBottom: 10 };
const createBox = { border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 12, background: 'var(--card-bg)' };
const formGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 };
const fieldLabel = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--text)' };
